// Modal for setting an on-call override. Launched two ways:
//   1. Clicking a future shift on the timeline — pre-fills the
//      override window with that shift's start/end so the admin's
//      typical "cover this slot" intent is one click away
//   2. The "Override now" button in the schedule row — pre-fills
//      with [now, end-of-current-shift]
//
// The form is small on purpose. Pick a user, optionally tweak the
// from/until times the modal proposed, type a reason if you like,
// save. Behind it sits setScheduleOverride which validates fromMs <
// untilMs and that the user is an org member.

import { useEffect, useMemo, useState } from 'react';
import { ShieldAlert } from 'lucide-react';
import { setScheduleOverride } from '../../lib/schedules';
import type { OrgMember, Schedule } from '../../lib/types';
import {
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  FormField,
  Input,
  Row,
  Stack,
} from '../ui';

export interface OverridePrefill {
  fromMs: number;
  untilMs: number;
  /** Optional — visible to the admin as "you're overriding this shift". */
  replacingUserDisplayName?: string;
}

interface Props {
  open: boolean;
  schedule: Schedule;
  orgId: string;
  members: OrgMember[];
  prefill: OverridePrefill | null;
  onClose: () => void;
  onSaved: () => void;
}

export function OverrideDialog({
  open,
  schedule,
  orgId,
  members,
  prefill,
  onClose,
  onSaved,
}: Props) {
  const [userId, setUserId] = useState('');
  const [fromIso, setFromIso] = useState('');
  const [untilIso, setUntilIso] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset the form whenever the dialog opens with new prefill data.
  // The prefill carries the clicked-shift bounds (or "now → end of
  // current shift" when launched from the row button).
  useEffect(() => {
    if (!open) return;
    setUserId('');
    setReason('');
    setError(null);
    if (prefill) {
      setFromIso(toLocalInputIso(prefill.fromMs));
      setUntilIso(toLocalInputIso(prefill.untilMs));
    }
  }, [open, prefill]);

  const sortedMembers = useMemo(
    () => [...members].sort((a, b) => (a.displayName ?? '').localeCompare(b.displayName ?? '')),
    [members],
  );

  const canSubmit =
    userId.length > 0 && fromIso.length > 0 && untilIso.length > 0 && !submitting;

  async function submit() {
    setError(null);
    const fromMs = new Date(fromIso).getTime();
    const untilMs = new Date(untilIso).getTime();
    if (!Number.isFinite(fromMs) || !Number.isFinite(untilMs)) {
      setError('Invalid date or time.');
      return;
    }
    if (fromMs >= untilMs) {
      setError('Override start must be before its end.');
      return;
    }
    if (untilMs <= Date.now()) {
      setError('Override end must be in the future.');
      return;
    }
    setSubmitting(true);
    try {
      await setScheduleOverride(orgId, schedule.id, {
        userId,
        fromMs,
        untilMs,
        reason: reason.trim() || undefined,
      });
      onSaved();
    } catch (err) {
      console.error('[override] save failed', err);
      setError(extractMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={submitting ? () => undefined : onClose}
      dismissOnBackdrop={!submitting}
      ariaLabel="Set on-call override"
    >
      <DialogHeader>Set on-call override</DialogHeader>
      <DialogBody>
        <Stack gap={3}>
          <Row gap={2} align="center">
            <ShieldAlert size={14} strokeWidth={2} color="var(--color-warning)" />
            <span className="ds-text-sm">
              Schedule:{' '}
              <strong style={{ fontWeight: 'var(--fw-semibold)' }}>{schedule.name}</strong>
            </span>
          </Row>

          {prefill?.replacingUserDisplayName && (
            <Row gap={2} align="center">
              <span className="ds-text-muted ds-text-sm">Covering</span>
              <Badge tone="neutral">{prefill.replacingUserDisplayName}</Badge>
            </Row>
          )}

          <FormField label="Replacement user" hint="Who will be on call during this window.">
            {(id) => (
              <select
                id={id}
                className="ds-input"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                required
              >
                <option value="">Pick a user…</option>
                {sortedMembers.map((m) => (
                  <option key={m.userId} value={m.userId}>
                    {m.displayName ?? m.email ?? m.userId}
                  </option>
                ))}
              </select>
            )}
          </FormField>

          <Row gap={2} align="start">
            <FormField label="From">
              {(id) => (
                <Input
                  id={id}
                  type="datetime-local"
                  value={fromIso}
                  onChange={(e) => setFromIso(e.target.value)}
                  required
                />
              )}
            </FormField>
            <FormField label="Until">
              {(id) => (
                <Input
                  id={id}
                  type="datetime-local"
                  value={untilIso}
                  onChange={(e) => setUntilIso(e.target.value)}
                  required
                />
              )}
            </FormField>
          </Row>

          <FormField label="Reason (optional)">
            {(id) => (
              <Input
                id={id}
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Alice on PTO"
                maxLength={200}
              />
            )}
          </FormField>

          {error && (
            <div
              style={{
                padding: 'var(--space-3) var(--space-4)',
                background: 'var(--color-danger-soft)',
                border: '1px solid var(--color-danger)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--color-danger-text)',
                fontSize: 'var(--fs-sm)',
              }}
            >
              {error}
            </div>
          )}
        </Stack>
      </DialogBody>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={submit}
          disabled={!canSubmit}
          loading={submitting}
        >
          Save override
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

/**
 * Convert an ms timestamp to the local-ISO format <input
 * type="datetime-local"> wants (no timezone suffix). We use the
 * user's local time for the picker — that's what they expect to see
 * when they say "until 6pm tonight". The backend stores ms so the
 * actual moment is unambiguous.
 */
function toLocalInputIso(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

function extractMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'message' in error) {
    const m = (error as { message: unknown }).message;
    if (typeof m === 'string' && m.length > 0) return m;
  }
  return 'Could not save the override. Try again.';
}
